import {
  postGameStartMutation,
  PostGameStartResponse,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { Button, toast } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setAreRostersOpen,
  setCurrentCatcher,
  setCurrentPitcher,
  setGameActive,
} from "../store/slices/game";
import { setSessionUuid } from "../store/slices/session";
import { setTrackingState } from "../store/slices/socket";
import type { RootState } from "../store/store";
import { handleErrorNotification } from "../utils/handleErrorNotification";
import { useUpdateGameState } from "../utils/useUpdateGameState";
import { toStartGameLineup } from "./startGameLineup";

export const GameSetupFooter = () => {
  const dispatch = useDispatch();
  const updateGameState = useUpdateGameState();
  const gameUuid = useSelector((state: RootState) => state.session.uuid);
  const { id: machineId } = useParams() as { id: string };

  const home = useSelector((state: RootState) => state.game.rosters.home);
  const away = useSelector((state: RootState) => state.game.rosters.away);

  const { mutate: startGameMutation, isPending } = useMutation({
    mutationFn: postGameStartMutation().mutationFn,
    onSuccess: (data: PostGameStartResponse) => {
      if (!data.gameState || !data.game) {
        throw new Error("Missing gameState or game in response");
      }

      dispatch(setSessionUuid(data.game.gameUuid));

      updateGameState(data.gameState, data.game);

      dispatch(
        setCurrentPitcher({ team: "away", player: away.currentPitcher }),
      );
      dispatch(
        setCurrentCatcher({ team: "away", player: away.currentCatcher }),
      );
      dispatch(
        setCurrentPitcher({ team: "home", player: home.currentPitcher }),
      );
      dispatch(
        setCurrentCatcher({ team: "home", player: home.currentCatcher }),
      );

      dispatch(setTrackingState(true));
      dispatch(setGameActive(true));
      dispatch(setAreRostersOpen(false));
    },
    onError: (error: Error) =>
      handleErrorNotification(
        "An error occurred while trying to start the game",
        error,
      ),
  });

  const handleStartGame = () => {
    if (away.roster.length === 0) {
      return toast("Away team needs at least one player on the roster", {
        variant: "error",
      });
    }
    if (home.roster.length === 0) {
      return toast("Home team needs at least one player on the roster", {
        variant: "error",
      });
    }

    if (gameUuid.length === 0) {
      startGameMutation({
        body: {
          awayTeamId: away.uuid,
          awayTeamLineup: toStartGameLineup(away),
          homeTeamId: home.uuid,
          homeTeamLineup: toStartGameLineup(home),
          machineUuid: machineId,
          type: "game",
        },
      });
    } else {
      dispatch(setAreRostersOpen(false));
    }
  };

  return (
    <div className="flex justify-center px-6 py-4 shadow-scroller-top">
      <Button
        variant="secondary-flat-light"
        onClick={handleStartGame}
        isLoading={isPending}
        className="px-12"
      >
        Start Game
      </Button>
    </div>
  );
};
